// UCC tab — "File UCC-1" via the Baselayer filing workspace. Thin wrapper:
// fetches the server-assembled debtor prefill for the loan, lets staff
// review/edit it plus the required collateral description and term, then
// creates a Bgc UCC-1 draft and navigates to the filing workspace where
// the standard procedure continues (approve → file → record filing #).
// EIN/SSN/TIN are deliberately never prefilled here.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Input, Label, Skeleton } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { uccPrefillApi } from "@/api/portfolioAdmin/uccPrefillApi";
import { useCreateBgcUccFiling } from "@/hooks/backgroundChecks/useBgcUccFiling";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";

interface FileUcc1FromLoanModalProps {
  loan: LoanSummaryDto;
  open: boolean;
  onClose: () => void;
}

interface DebtorForm {
  debtorLegalName: string;
  debtorType: string;
  debtorMailingLine1: string;
  debtorCity: string;
  debtorState: string;
  debtorZip: string;
  debtorJurisdictionOfOrg: string;
  filingState: string;
}

export function FileUcc1FromLoanModal({
  loan,
  open,
  onClose,
}: FileUcc1FromLoanModalProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateBgcUccFiling();

  const prefillQuery = useQuery({
    queryKey: ["portfolio-admin", "ucc-debtor-prefill", loan.id],
    queryFn: () => uccPrefillApi.getDebtorPrefill(loan.id),
    enabled: open,
  });
  const prefill = prefillQuery.data;

  const [form, setForm] = useState<DebtorForm | null>(null);
  const [collateral, setCollateral] = useState("");
  const [isLongTerm, setIsLongTerm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the editable summary once the prefill arrives (never re-clobbers).
  useEffect(() => {
    if (prefill && !form) {
      setForm({
        debtorLegalName: prefill.debtorLegalName ?? "",
        debtorType: prefill.debtorType ?? "",
        debtorMailingLine1: prefill.debtorMailingLine1 ?? "",
        debtorCity: prefill.debtorCity ?? "",
        debtorState: prefill.debtorState ?? "",
        debtorZip: prefill.debtorZip ?? "",
        debtorJurisdictionOfOrg: prefill.debtorJurisdictionOfOrg ?? "",
        filingState: prefill.filingState ?? "",
      });
    }
  }, [prefill, form]);

  const set = (key: keyof DebtorForm, next: string) =>
    setForm((prev) => (prev ? { ...prev, [key]: next } : prev));

  const onSubmit = async () => {
    if (!form || !prefill) return;
    setError(null);
    if (!form.debtorLegalName.trim()) {
      setError("Debtor legal name is required.");
      return;
    }
    if (!collateral.trim()) {
      setError("A collateral description is required.");
      return;
    }
    try {
      const created = await create.mutateAsync({
        filingType: "Ucc1",
        loanId: loan.id,
        dealId: prefill.dealId ?? loan.dealId,
        isLongTerm,
        filingState: form.filingState.trim() || null,
        debtorLegalName: form.debtorLegalName.trim(),
        debtorType: form.debtorType.trim() || null,
        debtorMailingLine1: form.debtorMailingLine1.trim() || null,
        debtorCity: form.debtorCity.trim() || null,
        debtorState: form.debtorState.trim() || null,
        debtorZip: form.debtorZip.trim() || null,
        debtorJurisdictionOfOrg: form.debtorJurisdictionOfOrg.trim() || null,
        securedPartyLegalName: prefill.securedPartyLegalName,
        collateralDescriptionText: collateral.trim(),
      });
      toast.success("UCC-1 draft created.");
      onClose();
      navigate(`/background-checks/ucc-filings/${created.id}`);
    } catch (err) {
      setError(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not create the UCC-1 draft.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="File UCC-1"
      description="Draft here, then approve, file with the filing office, and record the filing number."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={create.isPending || !form}
            data-testid="ucc1-from-loan-submit"
          >
            Create draft
          </Button>
        </>
      }
    >
      {prefillQuery.isLoading || (!form && !prefillQuery.isError) ? (
        <div className="flex flex-col gap-space-2" data-testid="ucc1-from-loan-loading">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : prefillQuery.isError || !form || !prefill ? (
        <p className="text-small text-status-error m-0" role="alert">
          Could not load the loan&apos;s debtor details. Close and retry.
        </p>
      ) : (
        <div className="flex flex-col gap-space-3" data-testid="ucc1-from-loan-form">
          <div className="grid grid-cols-2 gap-space-2">
            <div className="flex flex-col gap-space-1 col-span-2">
              <Label htmlFor="ucc1-debtor-name" required>Debtor legal name</Label>
              <Input
                id="ucc1-debtor-name"
                value={form.debtorLegalName}
                onChange={(e) => set("debtorLegalName", e.target.value)}
                data-testid="ucc1-debtor-name"
              />
            </div>
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="ucc1-debtor-line1">Mailing address</Label>
              <Input
                id="ucc1-debtor-line1"
                value={form.debtorMailingLine1}
                onChange={(e) => set("debtorMailingLine1", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="ucc1-debtor-city">City</Label>
              <Input
                id="ucc1-debtor-city"
                value={form.debtorCity}
                onChange={(e) => set("debtorCity", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="ucc1-debtor-state">State</Label>
              <Input
                id="ucc1-debtor-state"
                value={form.debtorState}
                onChange={(e) => set("debtorState", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="ucc1-debtor-zip">ZIP</Label>
              <Input
                id="ucc1-debtor-zip"
                value={form.debtorZip}
                onChange={(e) => set("debtorZip", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="ucc1-debtor-org">Jurisdiction of org</Label>
              <Input
                id="ucc1-debtor-org"
                value={form.debtorJurisdictionOfOrg}
                onChange={(e) => set("debtorJurisdictionOfOrg", e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="ucc1-filing-state">Filing state</Label>
              <Input
                id="ucc1-filing-state"
                value={form.filingState}
                onChange={(e) => set("filingState", e.target.value)}
                placeholder="DE / CA / …"
                data-testid="ucc1-filing-state"
              />
            </div>
          </div>

          <div className="flex flex-col gap-space-1">
            <span className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
              Secured party
            </span>
            <p className="text-small text-onyx m-0" data-testid="ucc1-secured-party">
              {prefill.securedPartyLegalName}
            </p>
          </div>

          <fieldset className="flex flex-col gap-space-1 border-0 p-0 m-0">
            <legend className="p-0 font-display text-label font-semibold uppercase tracking-label text-gray-mid mb-[6px]">
              Term
            </legend>
            <div className="flex items-center gap-space-4">
              <label className="flex items-center gap-space-2 text-small text-onyx">
                <input
                  type="radio"
                  name="ucc1-term"
                  checked={!isLongTerm}
                  onChange={() => setIsLongTerm(false)}
                  className="h-4 w-4 accent-gold"
                  data-testid="ucc1-term-standard"
                />
                Standard (5-year)
              </label>
              <label className="flex items-center gap-space-2 text-small text-onyx">
                <input
                  type="radio"
                  name="ucc1-term"
                  checked={isLongTerm}
                  onChange={() => setIsLongTerm(true)}
                  className="h-4 w-4 accent-gold"
                  data-testid="ucc1-term-long"
                />
                Long-term (30-year)
              </label>
            </div>
          </fieldset>

          <div className="flex flex-col gap-space-1">
            <Label htmlFor="ucc1-collateral" required>Collateral description</Label>
            <textarea
              id="ucc1-collateral"
              rows={4}
              className="w-full px-space-3 py-[10px] font-body text-small text-onyx bg-white-pure leading-normal border border-border-light rounded-md placeholder:text-gray-mid focus:outline-none focus:border-gold focus:shadow-goldRing resize-y"
              value={collateral}
              onChange={(e) => setCollateral(e.target.value)}
              data-testid="ucc1-collateral"
            />
          </div>

          <p className="text-caption text-gray-mid m-0" data-testid="ucc1-tax-id-note">
            EIN/SSN/TIN are never prefilled — add tax identifiers manually in
            the filing workspace if required.
          </p>

          {error && (
            <p className="text-small text-status-error m-0" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
